import React from 'react'
import * as yup from 'yup'
import { Autocomplete, Chip } from '@mui/material'
import { SpecialityOption } from '@/core/models/speciality-option.model'
import TextInput from '@/components/ui/inputs/TextInput'

export const specialityAutocompleteOptionSchema = yup.object({
  _id: yup.string(),
  code: yup.string().required(),
  name: yup.string().required(),
  selectByDefault: yup.boolean(),
  isNew: yup.boolean(),
  inputValue: yup.string(),
})

export type SpecialityAutocompleteOption = SpecialityOption & {
  isNew?: boolean
  inputValue?: string
}

export type SpecialitiesAutocompleteProps = {
  id: string
  name: string
  label: string
  editable: boolean
  selectedSpecialities: SpecialityAutocompleteOption[] | SpecialityAutocompleteOption | null
  specialities: SpecialityAutocompleteOption[]
  inputValue?: string
  onInputChange?: (value: string) => void
  onSpecialityChange: (event: any, newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null, reason: string) => void
  canDelete?: boolean
  multiple?: boolean
}

export default function SpecialitiesAutoCompleteComponent(props: SpecialitiesAutocompleteProps) {
  const {
    id,
    name,
    label,
    editable,
    selectedSpecialities,
    specialities,
    inputValue,
    onInputChange,
    onSpecialityChange,
    canDelete = true,
    multiple = true,
  } = props

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      value={selectedSpecialities}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      options={specialities}
      freeSolo
      autoHighlight
      autoComplete
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
          return option
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue
        }
        // Regular option
        return option.name
      }}
      filterOptions={(options, params) => {
        if (!options.length && params.inputValue !== '') {
          return [
            {
              inputValue: params.inputValue,
              isNew: true,
              name: `Add "${params.inputValue}"`,
              _id: '',
              code: '',
            },
          ]
        }
        return options
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.name}>
          {option.name}
        </li>
      )}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      onChange={onSpecialityChange}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { onDelete, ...rest } = getTagProps({ index })
          return (
            <Chip
              variant="outlined"
              label={option.name}
              onDelete={
                canDelete
                  ? () =>
                      onSpecialityChange(
                        null,
                        value.filter((v) => v !== option),
                        'removeOption'
                      )
                  : undefined
              }
              {...rest}
            />
          )
        })
      }
      disabled={!editable}
      renderInput={(params) => (
        <TextInput
          {...params}
          inputProps={{
            ...params.inputProps,
          }}
          label={label}
          name={name}
        />
      )}
      inputValue={inputValue}
      onInputChange={(event, value, reason) => onInputChange?.(value)}
    />
  )
}
